import Box from '@mui/material/Box';
import type { RefObject } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PublicPageBlocksView from 'src/common/components/PublicPageBlocksView';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import { IPageDocument } from 'src/helpers/pageDocumentUtils';
import { IHeroSettings } from 'src/interfaces/HeroSettings';
import {
  SOCIAL_SHARE_ALLOWED_BLOCK_TYPES,
  SOCIAL_SHARE_OG_HEIGHT,
  SOCIAL_SHARE_OG_WIDTH,
} from 'src/helpers/socialShareConstants';
import { LanguageKeys } from 'src/common/constants/languages';
import HeroBackgroundPanel from 'src/pages/Dashboard/System/Pages/components/HeroBackgroundPanel';
import PageDocumentEditor from 'src/pages/Dashboard/System/Pages/components/PageDocumentEditor';
import useSocialShareBlocks from 'src/hooks/useSocialShareBlocks';
import { ITrackMap } from 'src/interfaces/TrackMap';

const panelSx = {
  ...frostedPanelSx(0.5),
  p: { xs: 2, sm: 3 },
  mb: 2,
};

interface Props {
  composedDocument: IPageDocument;
  backgroundColor?: string;
  backgroundSettings?: IHeroSettings;
  previewLang: LanguageKeys;
  previewRef: RefObject<HTMLDivElement | null>;
  trackMaps: ITrackMap[];
  onDocumentChange: (doc: IPageDocument) => void;
  onBackgroundChange: (settings: IHeroSettings) => void;
}

export default function SocialShareComposer({
  composedDocument,
  backgroundColor,
  backgroundSettings,
  previewLang,
  previewRef,
  trackMaps,
  onDocumentChange,
  onBackgroundChange,
}: Props) {
  const { t } = useTranslation();
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const {
    document,
    blocks,
    selectedEditorLang,
    setSelectedEditorLang,
    setComposedDocument,
    onAddAt,
    onUpdate,
    onRemove,
    onMoveBlock,
    onMoveByDrag,
  } = useSocialShareBlocks(composedDocument, onDocumentChange);

  const handleDocumentChange = (doc: IPageDocument) => {
    setComposedDocument(doc);
  };

  useLayoutEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;

    const updateScale = () => {
      // Account for p:1 padding on the preview container.
      const width = container.clientWidth - 16;
      if (width > 0) {
        setPreviewScale(Math.min(1, width / SOCIAL_SHARE_OG_WIDTH));
      }
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <Box>
      <Paper variant="outlined" sx={panelSx}>
        <HeroBackgroundPanel
          heroSettings={backgroundSettings}
          onChange={onBackgroundChange}
          contentKey="social-share"
          contentType="socialShare"
          fieldName="composer-background"
        />
      </Paper>

      <Paper variant="outlined" sx={panelSx}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('system.socialSharing.composerTitle')}
        </Typography>
        <PageDocumentEditor
          document={document}
          onDocumentChange={handleDocumentChange}
          contentKey="social-share"
          selectedEditorLang={selectedEditorLang}
          setSelectedEditorLang={setSelectedEditorLang}
          trackMaps={trackMaps}
          showHeroZone={false}
          showBodyZone
          bodySectionTitleKey="system.socialSharing.blocksEditor"
          allowedBlockTypes={SOCIAL_SHARE_ALLOWED_BLOCK_TYPES}
          onMoveBlock={onMoveBlock}
          onMoveByDrag={onMoveByDrag}
          onUpdate={onUpdate}
          onRemove={onRemove}
          onAddAt={onAddAt}
        />
      </Paper>

      <Paper variant="outlined" sx={panelSx}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('system.socialSharing.previewSectionTitle')}
        </Typography>
        <Box
          ref={previewContainerRef}
          sx={{
            width: '100%',
            overflow: 'hidden',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: SOCIAL_SHARE_OG_WIDTH,
              aspectRatio: `${SOCIAL_SHARE_OG_WIDTH} / ${SOCIAL_SHARE_OG_HEIGHT}`,
              mx: 'auto',
              position: 'relative',
            }}
          >
            <Box
              ref={previewRef}
              data-social-share-og-preview
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: SOCIAL_SHARE_OG_WIDTH,
                height: SOCIAL_SHARE_OG_HEIGHT,
                transform: `scale(${previewScale})`,
                transformOrigin: 'top left',
                bgcolor: backgroundColor || '#9C5A37',
                backgroundImage: backgroundSettings?.backgroundImageUrl
                  ? `url(${backgroundSettings.backgroundImageUrl})`
                  : undefined,
                backgroundSize: 'cover',
                backgroundPosition: `${backgroundSettings?.backgroundAlignH ?? 'center'} ${backgroundSettings?.backgroundAlignV ?? 'center'}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'stretch',
                p: 3,
                boxSizing: 'border-box',
                overflow: 'hidden',
                '& > *': {
                  width: '100%',
                },
              }}
            >
              <PublicPageBlocksView
                blocks={blocks}
                lang={previewLang}
                contentKey="social-share-preview"
              />
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

